// src/admin/presets/api.ts

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

/**
 * Helper do autoryzowanych requestów
 */
async function fetchWithAuth(url: string, options: RequestInit = {}) {
  const token = localStorage.getItem('token');
  
  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': token ? `Bearer ${token}` : '',
    },
  });
}

/**
 * API client dla modułu presetów
 */
export const api = {
  /**
   * Uruchom proces harvestowania
   */
  async startHarvest(queries: string[] = [], limit: number = 40) {
    const response = await fetchWithAuth(`${API_URL}/api/presets/harvest`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ queries, limit }),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    return response.json();
  },

  /**
   * Pobierz listę kandydatów
   */
  async getCandidates(params: {
    status?: string;
    limit?: number;
    offset?: number;
  } = {}) {
    const queryParams = new URLSearchParams({
      status: params.status || 'pending',
      limit: String(params.limit || 50),
      offset: String(params.offset || 0),
    });
    
    const response = await fetchWithAuth(`${API_URL}/api/presets/candidates?${queryParams}`);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    return response.json();
  },

  /**
   * Aktualizuj kandydata
   */
  async updateCandidate(id: string, updates: any) {
    const response = await fetchWithAuth(`${API_URL}/api/presets/${id}/update`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    return response.json();
  },

  /**
   * Akceptuj kandydata
   */
  async acceptCandidate(id: string) {
    const response = await fetchWithAuth(`${API_URL}/api/presets/${id}/accept`, {
      method: 'POST',
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    return response.json();
  },

  /**
   * Odrzuć kandydata
   */
  async rejectCandidate(id: string) {
    const response = await fetchWithAuth(`${API_URL}/api/presets/${id}/reject`, {
      method: 'POST',
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    return response.json();
  },

  /**
   * Pobierz statystyki
   */
  async getStats() {
    const response = await fetchWithAuth(`${API_URL}/api/presets/stats`);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    return response.json();
  },

  /**
   * Pobierz listę presetów
   */
  async getPresets(params: {
    category?: string;
    limit?: number;
    offset?: number;
  } = {}) {
    const queryParams = new URLSearchParams();
    if (params.category) queryParams.set('category', params.category);
    queryParams.set('limit', String(params.limit || 50));
    queryParams.set('offset', String(params.offset || 0));
    
    const response = await fetchWithAuth(`${API_URL}/api/presets?${queryParams}`);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    return response.json();
  },

  /**
   * Pobierz kategorie
   */
  async getCategories() {
    const response = await fetchWithAuth(`${API_URL}/api/presets/categories`);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    return response.json();
  },

  /**
   * Kalkuluj cenę dla custom wymiarów
   */
  async calculatePrice(preset: any, dimensions?: any, extras?: any[]) {
    const response = await fetchWithAuth(`${API_URL}/api/presets/calculate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ preset, dimensions, extras }),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    return response.json();
  },
};
